import { Token } from '@kodiak-finance/sdk-core'
import Switch, { OptionType } from 'components/Switch'
import { useBaultAnalytics } from 'hooks/backend/baults'
import { useToken } from 'hooks/Tokens'
import { useConnection } from 'hooks/useConnection'
import { useGetTokenPrice } from 'hooks/useTokenPrice'
import useNativeCurrency from 'lib/hooks/useNativeCurrency'
import { FlexColumn } from 'pages/AddLiquidity/styled'
import { opacify } from 'polished'
import { useCallback, useMemo, useState } from 'react'
import { JSX } from 'react/jsx-runtime'
import { Area, AreaChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { IslandApiResponse } from 'state/data/slice'
import styled from 'styled-components'
import { useTheme } from 'styled-components'
import { formatDollarAmount } from 'utils/formatDollarAmt'
import { ExplorerDataType, getExplorerLink } from 'utils/getExplorerLink'
import { formatEther } from 'viem'

import { ThemedText } from '../../../../theme'
import { formatPercentage } from '../../../../utils/numbers'
import { FlexCol } from '../styleds'
import { FlexRowCenter } from '../styleds'
import ApyTooltip from './components/ApyTooltip'
import BaultCompoundTooltip from './components/BaultCustomTooltip'
import BeraPerRewardTooltip from './components/BeraPerRewardTooltip'
import Stat from './components/Stats'

interface BaultViewProps {
  baultId: string | undefined
  pool: IslandApiResponse | undefined
  timeframe?: '7' | '30' | '90' | 'max'
  rewardVaultRewardToken?: Token | null
}

interface ChartDataPoint {
  timestamp: number
  date: Date
  sharePrice: number
  compoundNumber?: number
  cumulativeGrowth?: number
  growthFromPrev?: string
  apy?: number
  dailyGrowth?: number
  compounds?: number
  beraPerReward?: number
  rewardAmount?: number
  bountyAmount?: number
  bountyValue?: number
  tokenAmount?: number
  isWrapperClaim?: boolean
}

const BaultStatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
  padding: 0 8px;
`

const ChartContainer = styled.div`
  height: 320px;
  margin: 16px 0;
  position: relative;
  padding: 18px 0;
  background: ${({ theme }) => theme.cardSecondary};
  border: 1px solid ${({ theme }) => theme.cardBorderPrimary};
  border-radius: 8px;

  .recharts-tooltip-cursor {
    fill: ${({ theme }) => opacify(20, theme.buttonPrimary)};
  }
`

const ToggleContainer = styled(FlexRowCenter)`
  margin-bottom: 16px;
`

const EmptyStateContainer = styled(FlexCol)`
  align-items: center;
  justify-content: center;
  height: 200px;
  color: ${({ theme }) => theme.textSecondary};
  text-align: center;
  padding: 0 20px;
`

const ErrorBanner = styled.div`
  margin: 8px;
  padding: 8px 12px;
  background: ${({ theme }) => theme.accentWarning + '20'};
  border: 1px solid ${({ theme }) => theme.accentWarning};
  border-radius: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`
const RetryButton = styled.button`
  background: none;
  border: 1px solid ${({ theme }) => theme.accentWarning};
  color: ${({ theme }) => theme.accentWarning};
  padding: 2px 8px;
  border-radius: 3px;
  cursor: pointer;
  font-size: 11px;

  &:hover {
    background: ${({ theme }) => theme.accentWarning + '10'};
  }
`

const generateEvenlySpacedTicks = (minValue: number, maxValue: number, tickCount = 6) => {
  const range = maxValue - minValue
  const buffer = range * 0.01
  const bufferedMin = minValue - buffer
  const bufferedMax = maxValue + buffer

  const step = (bufferedMax - bufferedMin) / (tickCount - 1)

  return Array.from({ length: tickCount }, (_, i) => {
    return Number((bufferedMin + i * step).toFixed(6))
  })
}

const CustomCompoundDot = (props: {
  cx: any
  cy: any
  payload: any
  index: any
  r?: number | undefined
  strokeWidth?: 1 | undefined
  explorerBaseUrl: any
  pricePoints: any
  dotColor?: string
}) => {
  const { chainId } = useConnection()
  // eslint-disable-next-line react/prop-types
  const { cx, cy, payload, index, r = 7, strokeWidth = 1, explorerBaseUrl, pricePoints, dotColor } = props
  const theme = useTheme()
  const [isHovering, setIsHovering] = useState(false)

  const baseColor = dotColor || theme.accentAction

  const originalPoint = useMemo(() => {
    if (payload && payload.compoundNumber && pricePoints) {
      return pricePoints.find((p: { compoundNumber: any }) => p.compoundNumber === payload.compoundNumber)
    }
    return null
  }, [payload, pricePoints])

  const hasLink = originalPoint && originalPoint?.transactionHash

  const explorerLink = useMemo(() => {
    if (!chainId) return
    return getExplorerLink(chainId, originalPoint?.transactionHash, ExplorerDataType.TRANSACTION)
  }, [chainId, originalPoint?.transactionHash])

  const handleClick = (e: { stopPropagation: () => void }) => {
    e.stopPropagation()
    if (hasLink) {
      window.open(explorerLink, '_blank')
    }
  }

  const dotRadius = isHovering ? r + 2 : r
  const fill = isHovering ? baseColor + '80' : baseColor

  return (
    <g
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      onClick={handleClick}
      style={{ cursor: hasLink ? 'pointer' : 'default' }}
    >
      {isHovering && (
        <circle cx={cx} cy={cy} r={r + 6} fill="transparent" stroke={baseColor} strokeWidth={1} opacity={0.5} />
      )}

      <circle
        cx={cx}
        cy={cy}
        r={dotRadius}
        fill={fill}
        stroke={theme.backgroundSurface}
        strokeWidth={strokeWidth}
        style={{ transition: 'all 0.2s ease' }}
      />

      {isHovering && hasLink && (
        <g transform={`translate(${cx - 3.5}, ${cy - 3.5}) scale(0.5)`}>
          <path
            d="M10 3H16V9M16 3L6 13"
            stroke="white"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </g>
      )}
    </g>
  )
}

const BaultView = ({ baultId, pool, timeframe, rewardVaultRewardToken }: BaultViewProps) => {
  const theme = useTheme()
  const nativeToken = useNativeCurrency()
  const rewardVaultRewardSymbol = rewardVaultRewardToken?.symbol

  const [chartType, setChartType] = useState<'price' | 'apy' | 'beraPerReward'>('price')

  const chartOptions: OptionType<'price' | 'apy' | 'beraPerReward'>[] = useMemo(
    () => [
      { value: 'price', label: 'Bault Price' },
      { value: 'apy', label: 'APY History' },
      { value: 'beraPerReward', label: `${rewardVaultRewardSymbol || 'Reward'}/BERA Cost` },
    ],
    [rewardVaultRewardSymbol]
  )

  const {
    data: analyticsData,
    isLoading: isAnalyticsLoading,
    error: analyticsError,
  } = useBaultAnalytics({ id: baultId ?? '' })

  const vault = analyticsData?.bault
  const pricePoints = analyticsData?.pricePoints || []
  // const dailySnapshots = analyticsData?.dailySnapshots || []
  // const weeklySnapshots = analyticsData?.weeklySnapshots || []

  const stakingToken = useToken(vault?.stakingToken?.id)

  const {
    price: beraPrice,
    isLoading: arePricesLoading,
    isError: hasPriceError,
    error: priceError,
    retry: retryPrices,
  } = useGetTokenPrice(nativeToken?.wrapped?.address)

  const stakingTokenPriceUSD = pool?.tokenLp?.price ?? 0

  const dataStates = useMemo(() => {
    const hasBasicData = Boolean(vault)
    const hasStakingPrice = Boolean(stakingTokenPriceUSD !== null)
    const hasBeraPrice = Boolean(beraPrice !== null)

    return {
      isAnalyticsLoading,
      hasAnalyticsError: Boolean(analyticsError),
      hasVaultData: Boolean(vault),

      arePricesLoading,
      hasPriceError,
      hasStakingPrice,
      hasBeraPrice,

      hasBasicData,
      isDataReady: hasBasicData && !isAnalyticsLoading,
      canShowStats: hasBasicData && !isAnalyticsLoading,
      canShowTVL: hasBasicData && hasStakingPrice && !arePricesLoading,
      canShowRewardChart: hasBasicData && hasBeraPrice && !arePricesLoading,

      retryPrices,
      errors: {
        analytics: analyticsError,
        prices: priceError,
      },
    }
  }, [
    vault,
    isAnalyticsLoading,
    analyticsError,
    arePricesLoading,
    hasPriceError,
    priceError,
    stakingTokenPriceUSD,
    beraPrice,
    retryPrices,
  ])

  const processedPricePoints = useMemo(() => {
    if (!pricePoints.length) return []

    const sortedPoints = [...pricePoints].sort((a, b) => a.compoundNumber - b.compoundNumber)
    const firstSharePrice = parseFloat(sortedPoints[0]?.sharePrice || '1')

    return sortedPoints.map((point, index): ChartDataPoint => {
      const prevPoint = index > 0 ? sortedPoints[index - 1] : point
      const growthFromPrev =
        index > 0
          ? (
              ((parseFloat(point.sharePrice) - parseFloat(prevPoint.sharePrice)) / parseFloat(prevPoint.sharePrice)) *
              100
            ).toFixed(8)
          : '0.00000000'

      const cumulativeGrowth = ((parseFloat(point.sharePrice) - firstSharePrice) / firstSharePrice) * 100

      return {
        sharePrice: parseFloat(point.sharePrice),
        compoundNumber: point.compoundNumber,
        timestamp: parseInt(point.timestamp) * 1000,
        date: new Date(parseInt(point.timestamp) * 1000),
        growthFromPrev,
        cumulativeGrowth,
      }
    })
  }, [pricePoints])

  const processedCompoundAPY = useMemo(() => {
    if (!pricePoints.length) return []

    const sortedPoints = [...pricePoints].sort((a, b) => a.compoundNumber - b.compoundNumber)

    return sortedPoints.map(
      (point): ChartDataPoint => ({
        timestamp: parseInt(point.timestamp) * 1000,
        date: new Date(parseInt(point.timestamp) * 1000),
        compoundNumber: point.compoundNumber,
        apy: parseFloat(point.apy),
        sharePrice: parseFloat(point.sharePrice),
      })
    )
  }, [pricePoints])

  const processedBeraPerRewardData = useMemo(() => {
    if (!pricePoints.length || !vault || !dataStates.hasStakingPrice || !dataStates.hasBeraPrice) return []

    const sortedPoints = [...pricePoints].sort((a, b) => a.compoundNumber - b.compoundNumber)

    return sortedPoints.map((point): ChartDataPoint => {
      const rewardAmount = BigInt(point.bgtAmount || '0')
      const wrapperTokenAmount = BigInt(point.wrapperTokenAmount || '0')

      // Use the historical bounty amount from the price point instead of current vault bounty
      const bountyAmount = parseFloat(point.bountyAmount || vault.bounty) / 1e18
      const dollarValue = bountyAmount * stakingTokenPriceUSD

      const tokenAmount = point.isWrapperClaim
        ? Number(formatEther(wrapperTokenAmount))
        : Number(formatEther(rewardAmount))

      // Formula: ($ value of bounty cost) / (reward amount) / (BERA price)
      const beraPriceValue = beraPrice ?? 0
      const beraPerReward = tokenAmount > 0 && beraPriceValue > 0 ? dollarValue / tokenAmount / beraPriceValue : 0

      return {
        timestamp: parseInt(point.timestamp) * 1000,
        date: new Date(parseInt(point.timestamp) * 1000),
        compoundNumber: point.compoundNumber,
        beraPerReward,
        sharePrice: parseFloat(point.sharePrice),
        bountyAmount,
        tokenAmount,
        bountyValue: dollarValue,
        isWrapperClaim: point.isWrapperClaim,
      }
    })
  }, [pricePoints, vault, stakingTokenPriceUSD, beraPrice, dataStates.hasStakingPrice, dataStates.hasBeraPrice])

  const chartData = useMemo(() => {
    if (!processedPricePoints.length) return []

    if (chartType === 'price') {
      const compoundMap = new Map()
      processedPricePoints.forEach((point) => {
        if (point.compoundNumber !== undefined) {
          compoundMap.set(point.timestamp, true)
        }
      })

      const basePoints = 300
      const maxPoints = 800

      if (processedPricePoints.length <= basePoints) {
        return processedPricePoints
      }

      const compoundCount = compoundMap.size
      const regularPointsToInclude = Math.min(basePoints, maxPoints - compoundCount)
      const regularPointCount = processedPricePoints.length - compoundCount
      const sampleRate = Math.max(1, Math.floor(regularPointCount / regularPointsToInclude))

      return processedPricePoints.filter((point, i) => {
        const isCompoundEvent = point.compoundNumber !== undefined
        const isFirstOrLast = i === 0 || i === processedPricePoints.length - 1
        const isRegularSample = i % sampleRate === 0

        return isCompoundEvent || isFirstOrLast || isRegularSample
      })
    }

    if (chartType === 'apy') {
      return processedCompoundAPY
    }

    if (chartType === 'beraPerReward') {
      return processedBeraPerRewardData
    }

    return []
  }, [processedPricePoints, chartType, processedCompoundAPY, processedBeraPerRewardData])

  const filteredChartData = useMemo(() => {
    if (!chartData.length) return []

    const now = Date.now()
    const msPerDay = 86400000

    if (timeframe === '7') {
      const sevenDaysAgo = now - 7 * msPerDay

      const filteredData = chartData.filter((point) => point.timestamp >= sevenDaysAgo)

      if (filteredData.length === 0 && chartData.length > 0) {
        return chartData.slice(-Math.min(chartData.length, 10))
      }

      return filteredData
    } else if (timeframe === '30') {
      return chartData.filter((point) => now - point.timestamp < 30 * msPerDay)
    } else if (timeframe === '90') {
      return chartData.filter((point) => now - point.timestamp < 90 * msPerDay)
    }

    return chartData
  }, [chartData, timeframe])

  const vaultMetrics = useMemo(() => {
    if (!vault) return null

    const vaultAgeSeconds = parseInt(vault.apyTimestamp) - parseInt(vault.createdAt)
    const vaultAgeDays = vaultAgeSeconds / (24 * 60 * 60)
    const compoundsPerDay = vault.compoundCount / vaultAgeDays
    const avgCompoundIntervalSeconds = vault.averageCompoundInterval
      ? parseInt(vault.averageCompoundInterval)
      : vaultAgeSeconds / vault.compoundCount

    return {
      apy: parseFloat(vault.apy),
      sharePrice: parseFloat(vault.sharePrice),
      compoundCount: vault.compoundCount,
      compoundFeeBps: vault.compoundFeeBps,
      compoundFeePercent: vault.compoundFeeBps / 100,
      vaultAgeDays,
      compoundsPerDay,
      avgCompoundIntervalSeconds,
      totalAssets: vault.totalAssets,
      createdAt: parseInt(vault.createdAt),
      apyTimestamp: parseInt(vault.apyTimestamp),
    }
  }, [vault])

  const formattedStats = useMemo(() => {
    if (!vaultMetrics || !vault) return null

    return {
      apy: `${formatPercentage(vaultMetrics.apy.toString())}`,
      sharePrice: vaultMetrics.sharePrice.toFixed(8),
      totalCompounds: vaultMetrics.compoundCount.toLocaleString(),
      compoundsPerDay: vaultMetrics.compoundsPerDay.toFixed(2),
      compoundInterval: `${vaultMetrics.avgCompoundIntervalSeconds.toFixed(0)}s`,
      vaultAge: `${vaultMetrics.vaultAgeDays.toFixed(1)} days`,
      totalAssets: parseInt(vaultMetrics.totalAssets),
      tvl: Number(formatEther(BigInt(vaultMetrics.totalAssets))) * stakingTokenPriceUSD,
      fee: `${vaultMetrics.compoundFeePercent}%`,
    }
  }, [vaultMetrics, vault, stakingTokenPriceUSD])

  const filterOutliers = useCallback((data: ChartDataPoint[], upperThreshold = 4, lowerThreshold = 0) => {
    const filteredData = data.filter(
      (point) =>
        point.beraPerReward !== undefined &&
        point.beraPerReward !== null &&
        point.beraPerReward <= upperThreshold &&
        point.beraPerReward >= lowerThreshold
    )

    const outliers = data.filter(
      (point) =>
        point.beraPerReward !== undefined &&
        point.beraPerReward !== null &&
        (point.beraPerReward > upperThreshold || point.beraPerReward < lowerThreshold)
    )

    return { filteredData, outliers }
  }, [])

  const formatAxisTimestamp = useCallback(
    (timestamp: number): string => {
      const date = new Date(timestamp)
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      const month = months[date.getMonth()]
      const day = date.getDate()

      if (chartType === 'apy') {
        return `${month} ${day}`
      }

      const shouldShowTime = timeframe === '7' || filteredChartData.length < 20

      if (shouldShowTime && filteredChartData.length < 10) {
        const hours = date.getHours().toString().padStart(2, '0')
        const minutes = date.getMinutes().toString().padStart(2, '0')
        return `${month} ${day}\n${hours}:${minutes}`
      }

      return `${month} ${day}`
    },
    [timeframe, filteredChartData.length, chartType]
  )

  const getDailyTicks = useCallback((data: ChartDataPoint[]): number[] => {
    if (!data.length) return []

    const dailyMap = new Map<string, number>()

    data.forEach((point) => {
      const date = new Date(point.timestamp)
      const dayKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`

      if (!dailyMap.has(dayKey)) {
        dailyMap.set(dayKey, point.timestamp)
      }
    })

    return Array.from(dailyMap.values()).sort((a, b) => a - b)
  }, [])

  if (!baultId) {
    return (
      <EmptyStateContainer>
        <ThemedText.DeprecatedBody>No vault ID provided</ThemedText.DeprecatedBody>
      </EmptyStateContainer>
    )
  }

  const renderChart = () => {
    if (dataStates.isAnalyticsLoading) {
      return (
        <EmptyStateContainer>
          <ThemedText.DeprecatedBody>Loading vault data...</ThemedText.DeprecatedBody>
        </EmptyStateContainer>
      )
    }

    if (dataStates.hasAnalyticsError || !filteredChartData.length) {
      return (
        <EmptyStateContainer>
          <ThemedText.DeprecatedBody>
            {dataStates.hasAnalyticsError
              ? 'Error loading data. Please try again later.'
              : 'No data available for the selected timeframe'}
          </ThemedText.DeprecatedBody>
        </EmptyStateContainer>
      )
    }

    if (chartType === 'price') {
      const yMin = Math.min(...filteredChartData.map((d) => d.sharePrice))
      const yMax = Math.max(...filteredChartData.map((d) => d.sharePrice))

      const yTicks = generateEvenlySpacedTicks(yMin, yMax)

      const CompoundDot = (
        props: JSX.IntrinsicAttributes & {
          cx: any
          cy: any
          payload: any
          index: any
          r?: 2 | undefined
          strokeWidth?: 1 | undefined
          explorerBaseUrl: any
          pricePoints: any
        }
      ) => <CustomCompoundDot {...props} pricePoints={pricePoints} r={3} strokeWidth={1} />

      return (
        <ChartContainer>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={filteredChartData} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.backgroundOutline} />

              <XAxis
                dataKey="timestamp"
                type="number"
                scale="time"
                domain={['dataMin', 'dataMax']}
                ticks={getDailyTicks(filteredChartData)}
                tickFormatter={(timestamp) => formatAxisTimestamp(timestamp)}
                tick={{ fill: theme.textSecondary, fontSize: 11 }}
                stroke={theme.backgroundOutline}
                height={50}
              />

              <YAxis
                domain={[yTicks[0], yTicks[yTicks.length - 1]]}
                ticks={yTicks}
                tickFormatter={(value) => value.toFixed(6)}
                tick={{ fill: theme.textSecondary, fontSize: 12 }}
                stroke={theme.backgroundOutline}
              />
              <Tooltip
                content={
                  <BaultCompoundTooltip
                    pricePoints={pricePoints}
                    stakingToken={stakingToken}
                    rewardVaultRewardToken={rewardVaultRewardToken}
                  />
                }
              />

              <Line
                type="monotone"
                dataKey="sharePrice"
                name="Compound Event"
                data={filteredChartData.filter((point) => point.compoundNumber !== undefined)}
                stroke="transparent"
                dot={CompoundDot}
                activeDot={(props: any) => <CompoundDot {...props} r={9} strokeWidth={2} />}
                connectNulls={false}
                isAnimationActive={false}
              />

              <Line
                type="monotone"
                dataKey="sharePrice"
                name="Share Price"
                stroke={theme.accentAction}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 6, fill: theme.accentAction, stroke: theme.backgroundSurface }}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartContainer>
      )
    }

    if (chartType === 'apy') {
      const maxVisibleDots = 300
      const totalPoints = filteredChartData.length
      const skipRatio = totalPoints > maxVisibleDots ? Math.ceil(totalPoints / maxVisibleDots) : 1

      const ConditionalApyDot = (props: any) => {
        const { index } = props

        const isFirstOrLast = index === 0 || index === totalPoints - 1
        const isNthPoint = index % skipRatio === 0
        const shouldRenderDot = totalPoints <= maxVisibleDots || isFirstOrLast || isNthPoint

        if (!shouldRenderDot) {
          return <circle r={0} opacity={0} />
        }

        return <CustomCompoundDot {...props} pricePoints={pricePoints} r={3} dotColor={theme.accentSuccess} />
      }

      return (
        <ChartContainer>
          <div
            style={{
              textAlign: 'center',
              fontSize: '12px',
              color: theme.textSecondary,
              marginBottom: '8px',
              padding: '0 20px',
            }}
          >
            APY is calculated based on the last 10 compounds, annualized
          </div>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={filteredChartData} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.backgroundOutline} />
              <XAxis
                dataKey="timestamp"
                type="number"
                scale="time"
                domain={['dataMin', 'dataMax']}
                ticks={getDailyTicks(filteredChartData)}
                tickFormatter={(timestamp) => formatAxisTimestamp(timestamp)}
                tick={{ fill: theme.textSecondary, fontSize: 12 }}
                stroke={theme.backgroundOutline}
              />
              <YAxis
                tickFormatter={(value) => `${value.toFixed(0)}%`}
                tick={{ fill: theme.textSecondary, fontSize: 12 }}
                stroke={theme.backgroundOutline}
              />
              <Tooltip content={<ApyTooltip />} />
              <Area
                type="monotone"
                dataKey="apy"
                name="Fill"
                stroke="transparent"
                fill={theme.accentSuccessSoft}
                fillOpacity={0.6}
                dot={ConditionalApyDot}
                activeDot={(props: any) => <ConditionalApyDot {...props} r={6} strokeWidth={2} />}
              />
              <Area
                type="monotone"
                dataKey="apy"
                name="APY"
                stroke={theme.accentSuccess}
                fill="transparent"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 6, fill: theme.accentSuccess, stroke: theme.backgroundSurface }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartContainer>
      )
    }

    if (chartType === 'beraPerReward') {
      if (!dataStates.canShowRewardChart) {
        return (
          <EmptyStateContainer>
            <ThemedText.DeprecatedBody>
              {dataStates.arePricesLoading
                ? 'Loading price data...'
                : dataStates.hasPriceError
                ? 'Price data unavailable'
                : !dataStates.hasBeraPrice
                ? `BERA price required for ${rewardVaultRewardSymbol || 'reward'} cost analysis`
                : `${rewardVaultRewardSymbol || 'Reward'}/BERA chart data unavailable`}
            </ThemedText.DeprecatedBody>
            {dataStates.hasPriceError && (
              <RetryButton onClick={dataStates.retryPrices} style={{ marginTop: '8px' }}>
                Retry Price Loading
              </RetryButton>
            )}
          </EmptyStateContainer>
        )
      }

      const hasNonZeroRewardValues = processedBeraPerRewardData.some((point) => (point?.beraPerReward ?? 0) > 0.0001)

      if (!hasNonZeroRewardValues) {
        return (
          <EmptyStateContainer>
            <ThemedText.DeprecatedBody>
              No {rewardVaultRewardSymbol} reward data available. This may occur if all compounds used wrapper claims or
              the {rewardVaultRewardSymbol || 'reward'} amount data is not available.
            </ThemedText.DeprecatedBody>
          </EmptyStateContainer>
        )
      }
      const yTicks = [0.8, 1.0, 1.2, 1.4, 1.6, 1.8, 2.0]
      const { filteredData: chartDataWithoutOutliers, outliers } = filterOutliers(
        filteredChartData,
        yTicks[yTicks.length - 1],
        yTicks[0]
      )

      const RewardClickableDot = (
        props: JSX.IntrinsicAttributes & {
          cx: any
          cy: any
          payload: any
          index: any
          r?: 4
          strokeWidth?: 1
          explorerBaseUrl: any
          pricePoints: any
        }
      ) => <CustomCompoundDot {...props} pricePoints={pricePoints} r={4} />

      return (
        <ChartContainer>
          <div
            style={{
              textAlign: 'center',
              fontSize: '12px',
              color: theme.textSecondary,
              marginBottom: '8px',
              padding: '0 20px',
            }}
          >
            This chart shows the effective {rewardVaultRewardSymbol || 'reward'} premium realized in the compound
          </div>

          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartDataWithoutOutliers} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.backgroundOutline} />
              <XAxis
                dataKey="timestamp"
                type="number"
                scale="time"
                domain={['dataMin', 'dataMax']}
                ticks={getDailyTicks(chartDataWithoutOutliers)}
                tickFormatter={(timestamp) => formatAxisTimestamp(timestamp)}
                tick={{ fill: theme.textSecondary, fontSize: 11 }}
                stroke={theme.backgroundOutline}
                height={50}
              />
              <YAxis
                domain={[yTicks[0], yTicks[yTicks.length - 1]]}
                ticks={yTicks}
                tickFormatter={(value) => value.toFixed(1)}
                tick={{ fill: theme.textSecondary, fontSize: 12 }}
                stroke={theme.backgroundOutline}
              />
              <Tooltip
                content={
                  <BeraPerRewardTooltip
                    pricePoints={pricePoints}
                    stakingToken={stakingToken}
                    rewardVaultRewardSymbol={rewardVaultRewardSymbol}
                  />
                }
              />

              <Line
                type="monotone"
                dataKey="beraPerReward"
                name="Dots"
                stroke="transparent"
                dot={RewardClickableDot}
                activeDot={(props: any) => <RewardClickableDot {...props} r={6} strokeWidth={2} />}
              />

              <Line
                type="monotone"
                dataKey="beraPerReward"
                name={`${rewardVaultRewardSymbol || 'Reward'}/BERA Cost`}
                stroke={theme.accentAction}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 6, fill: theme.accentAction, stroke: theme.backgroundSurface }}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartContainer>
      )
    }
    return null
  }

  return (
    <>
      <BaultStatsContainer>
        <Stat
          label="Current APY"
          value={formattedStats?.apy}
          color={theme.accentSuccess}
          isLoading={dataStates.isAnalyticsLoading}
          hasError={dataStates.hasAnalyticsError || (!dataStates.isAnalyticsLoading && !dataStates.hasBasicData)}
          errorMessage="Failed to load vault data"
        />

        <Stat
          label="TVL"
          value={formattedStats?.tvl !== undefined ? formatDollarAmount(formattedStats.tvl) : undefined}
          isLoading={dataStates.isAnalyticsLoading || dataStates.arePricesLoading || !dataStates.hasStakingPrice}
          hasError={Boolean(dataStates.hasPriceError)}
          errorMessage={dataStates.hasPriceError ? 'Price data unavailable' : 'Failed to calculate TVL'}
        />

        <Stat
          label="Bault Price"
          value={formattedStats?.sharePrice}
          isLoading={dataStates.isAnalyticsLoading}
          hasError={dataStates.hasAnalyticsError || (!dataStates.isAnalyticsLoading && !dataStates.hasBasicData)}
          errorMessage="Failed to load vault data"
        />

        <Stat
          label="Total Compounds"
          value={formattedStats?.totalCompounds}
          subValue={formattedStats ? `${formattedStats.compoundsPerDay}/day` : undefined}
          isLoading={dataStates.isAnalyticsLoading}
          hasError={dataStates.hasAnalyticsError || (!dataStates.isAnalyticsLoading && !dataStates.hasBasicData)}
          errorMessage="Failed to load vault data"
        />

        <Stat
          label="Vault Age"
          value={formattedStats?.vaultAge}
          isLoading={dataStates.isAnalyticsLoading}
          hasError={dataStates.hasAnalyticsError || (!dataStates.isAnalyticsLoading && !dataStates.hasBasicData)}
          errorMessage="Failed to load vault data"
        />
      </BaultStatsContainer>

      {dataStates.hasPriceError && (
        <ErrorBanner>
          <ThemedText.DeprecatedSmall color={theme.accentWarning}>
            Price data temporarily unavailable - some features may be limited
          </ThemedText.DeprecatedSmall>
          <RetryButton onClick={dataStates.retryPrices}>Retry</RetryButton>
        </ErrorBanner>
      )}

      <ToggleContainer>
        <Switch<typeof chartType> options={chartOptions} activeValue={chartType} setActiveValue={setChartType} />
      </ToggleContainer>

      <FlexColumn>{renderChart()}</FlexColumn>
    </>
  )
}

export default BaultView
